import React, { useEffect, useState, useCallback } from 'react'
import { fetchDemo, uploadThumbnail, updateDemo, UpgradeRequiredError } from './api'
import { useEditorStore } from './useEditorStore'
import type { Demo } from './types'
import BlockLibrary from './components/BlockLibrary'
import Canvas from './components/Canvas'
import PropertiesPanel from './components/PropertiesPanel'
import BackgroundEditor from './components/BackgroundEditor'
import ThemeEditor from './components/ThemeEditor'
import EditorHeader from './components/EditorHeader'
import PublishModal from './components/modals/PublishModal'
import ShareModal from './components/modals/ShareModal'
import UpgradeModal from './components/modals/UpgradeModal'
import type { UpgradeReason } from './components/modals/UpgradeModal'
import { X, Loader2 } from 'lucide-react'
import { toPng } from 'html-to-image'
import { fireConfetti } from './confetti'
import OnboardingChecklist from './components/OnboardingChecklist'
import BadgeToast from './components/BadgeToast'
import type { NewBadge } from './components/BadgeToast'

export type UserPlan = 'free' | 'pro' | 'enterprise'

interface EditorAppProps {
  demoId?: string
  userPlan?: UserPlan
  isGuest?: boolean
}

export default function EditorApp({ demoId, userPlan = 'free', isGuest = false }: EditorAppProps) {
  const [demo, setDemo] = useState<Demo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!demoId) return
    fetchDemo(demoId)
      .then(setDemo)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [demoId])

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#f6f6f8]">
        <Loader2 className="h-8 w-8 animate-spin text-[#5048e5]" />
      </div>
    )
  }

  if (error || !demo) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#f6f6f8]">
        <p className="text-red-500">{error || 'Demo not found'}</p>
      </div>
    )
  }

  return <EditorInner demo={demo} userPlan={userPlan} isGuest={isGuest} />
}

function EditorInner({ demo, userPlan, isGuest }: { demo: Demo; userPlan: UserPlan; isGuest: boolean }) {
  const store = useEditorStore(String(demo.id), demo.content, demo.slug, demo.published)
  const [showPublishModal, setShowPublishModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [upgradeReasons, setUpgradeReasons] = useState<UpgradeReason[] | null>(null)
  const [showDesignPanel, setShowDesignPanel] = useState<'background' | 'theme' | null>(null)
  const [newBadges, setNewBadges] = useState<NewBadge[] | null>(null)
  const canvasContentRef = React.useRef<HTMLDivElement | null>(null)

  const linkedBlocks = store.selectedBlock?.variable
    ? store.getLinkedBlocks(store.selectedBlock.variable)
    : []

  const stepVisibility = store.getStepVisibility()
  const publicUrl = `/d/${store.slug}`

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable
      const isInlineEditor = target.closest('[data-inline-editor]') !== null

      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !isInlineEditor && !e.shiftKey) { e.preventDefault(); store.undo(); return }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !isInlineEditor && e.shiftKey) { e.preventDefault(); store.redo(); return }
      if ((e.metaKey || e.ctrlKey) && e.key === 'd') { e.preventDefault(); if (store.selectedBlockId) store.duplicateBlock(store.selectedBlockId); return }

      if (isInput) return
      if ((e.key === 'Delete' || e.key === 'Backspace') && store.selectedBlockId) { e.preventDefault(); store.removeBlock(store.selectedBlockId); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); store.selectNextBlock(-1) }
      else if (e.key === 'ArrowDown') { e.preventDefault(); store.selectNextBlock(1) }
      if (e.key === 'Escape') store.setSelectedBlockId(null)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [store])

  const handlePreview = useCallback(() => window.open(publicUrl, '_blank'), [publicUrl])

  const handlePublish = useCallback(async (data: { title: string; description: string; categoryIds: number[] }) => {
    try {
      // Save title/description before publishing
      await updateDemo(String(demo.id), { title: data.title, description: data.description })

      // Capture thumbnail before publishing
      if (canvasContentRef.current) {
        try {
          const dataUrl = await toPng(canvasContentRef.current, {
            pixelRatio: 1,
            cacheBust: true,
            skipFonts: true,
            width: 800,
            height: 600,
          })
          await uploadThumbnail(String(demo.id), dataUrl)
        } catch (e) {
          console.error('Thumbnail capture failed:', e)
        }
      }
      const result = await store.publish(data.categoryIds)
      setShowPublishModal(false)
      fireConfetti()
      if (result?.new_badges?.length) {
        setNewBadges(result.new_badges)
      }
    } catch (err) {
      if (err instanceof UpgradeRequiredError) {
        setShowPublishModal(false)
        setUpgradeReasons(err.reasons)
      }
    }
  }, [store.publish, demo.id])

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-[#f6f6f8] text-slate-900">
      {showPublishModal && (
        <PublishModal
          title={demo.title}
          description={demo.description || ''}
          onConfirm={handlePublish}
          onCancel={() => setShowPublishModal(false)}
          publishing={store.publishing}
        />
      )}
      {showShareModal && (
        <ShareModal slug={store.slug} canvasRef={canvasContentRef} onClose={() => setShowShareModal(false)} />
      )}
      {upgradeReasons && (
        <UpgradeModal reasons={upgradeReasons} onClose={() => setUpgradeReasons(null)} />
      )}

      <EditorHeader
        title={demo.title}
        saving={store.saving}
        published={store.published}
        publishing={store.publishing}
        canUndo={store.canUndo}
        canRedo={store.canRedo}
        showDesignPanel={showDesignPanel}
        forkedFrom={demo.forked_from}
        isGuest={isGuest}
        onUndo={store.undo}
        onRedo={store.redo}
        onShare={() => setShowShareModal(true)}
        onPreview={handlePreview}
        onPublish={() => setShowPublishModal(true)}
        onToggleDesignPanel={setShowDesignPanel}
      />

      <main className="flex flex-1 overflow-hidden">
        <BlockLibrary
          onAddBlock={store.addBlock}
          onAddTemplate={store.addTemplate}
          variables={store.variables}
          variableValues={store.variableValues}
          onAddFormulaVariable={store.addFormulaVariable}
          onUpdateFormulaVariable={store.updateFormulaVariable}
          onRemoveFormulaVariable={store.removeFormulaVariable}
          userPlan={userPlan}
        />

        <Canvas
          blocks={store.blocks}
          variables={store.variables}
          variableValues={store.variableValues}
          selectedBlockId={store.selectedBlockId}
          onSelectBlock={store.setSelectedBlockId}
          onMoveBlock={store.moveBlock}
          onRemoveBlock={store.removeBlock}
          onUpdateBlock={store.updateBlock}
          onSetVariableValue={store.setVariableValue}
          saving={store.saving}
          background={store.background}
          theme={store.theme}
          stepVisibility={stepVisibility}
          canvasContentRef={canvasContentRef}
        />

        <div className="flex shrink-0">
          <PropertiesPanel
            block={store.selectedBlock}
            blocks={store.blocks}
            variables={store.variables}
            onUpdateBlock={store.updateBlock}
            onRemoveBlock={store.removeBlock}
            onDuplicateBlock={store.duplicateBlock}
            onResetBlock={store.resetBlock}
            onUpdateVariable={store.updateVariable}
            onRenameVariable={store.renameVariable}
            onAddVariable={store.addVariable}
            linkedBlocks={linkedBlocks}
          />

          {showDesignPanel && (
            <aside className="w-72 border-l border-slate-200 bg-white p-5 overflow-y-auto shrink-0">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-slate-900">
                  {showDesignPanel === 'background' ? 'Background' : 'Theme'}
                </h3>
                <button onClick={() => setShowDesignPanel(null)} className="p-1 text-slate-400 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
              {showDesignPanel === 'background' && (
                <BackgroundEditor background={store.background} onSetBackground={store.setBackground} />
              )}
              {showDesignPanel === 'theme' && (
                <ThemeEditor theme={store.theme} onSetTheme={store.setTheme} />
              )}
            </aside>
          )}
        </div>
      </main>

      {newBadges && (
        <BadgeToast badges={newBadges} onDone={() => setNewBadges(null)} />
      )}

      {!isGuest && (
        <OnboardingChecklist
          blocks={store.blocks}
          variables={store.variables}
          published={store.published}
        />
      )}
    </div>
  )
}
